import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { designSystem } from '../../design-system';

// プロダクト画像のインポート
import ucartImage from '../../assets/images/project_ucart.png';
import fairsImage from '../../assets/images/project_Fairs.png';
import fairsAdminImage from '../../assets/images/project_Fairs_admin.png';
import wuuImage from '../../assets/images/project_wuu.png';
import synchroImage from '../../assets/images/project_synchro.png';

const ProductsContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: designSystem.colors.lightBg,
  padding: '100px 120px',
  overflow: 'visible',

  '@media (max-width: 1600px)': {
    padding: '90px 100px',
  },

  '@media (max-width: 1440px)': {
    padding: '80px 80px',
  },

  '@media (max-width: 1024px)': {
    padding: '70px 60px',
  },

  '@media (max-width: 768px)': {
    padding: '60px 24px',
  },
});

const ProductsContent = styled(Box)({
  position: 'relative',
  width: '100%',
  margin: '0',
  padding: '0',

  '@media (max-width: 768px)': {
    width: '100%',
  },
});

const SectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '48px',
  width: '100%',

  '@media (max-width: 1600px)': {
    marginBottom: '44px',
  },

  '@media (max-width: 1440px)': {
    marginBottom: '40px',
  },

  '@media (max-width: 1024px)': {
    marginBottom: '36px',
  },

  '@media (max-width: 768px)': {
    marginBottom: '32px',
  },
});

const SectionTitle = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '24px',
  fontWeight: 500,
  color: designSystem.colors.grayText,
  letterSpacing: '0.5px',

  '@media (max-width: 1600px)': {
    fontSize: '22px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '20px',
  },

  '@media (max-width: 768px)': {
    fontSize: '18px',
  },
});

const ProductCounter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const CounterText = styled(Typography)(({ active }) => ({
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '24px',
  fontWeight: 500,
  color: designSystem.colors.grayText,
  opacity: active ? 1 : 0.3,
  letterSpacing: '0.5px',

  '@media (max-width: 1600px)': {
    fontSize: '22px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '20px',
  },

  '@media (max-width: 768px)': {
    fontSize: '18px',
  },
}));

const CounterDivider = styled(Box)({
  width: '24px',
  height: '1px',
  backgroundColor: designSystem.colors.grayText,
  transform: 'rotate(180deg)',
});

const NavIcon = styled('img')({
  position: 'absolute',
  top: 'calc(50% + 40px)',
  transform: 'translateY(-50%)',
  width: '80px',
  height: '80px',
  transition: 'all 0.4s ease',
  zIndex: 10,
  objectFit: 'contain',

  '&:hover': {
    opacity: 0.7,
    transform: 'translateY(-50%) scale(1.1)',
  },

  '@media (max-width: 1600px)': {
    width: '72px',
    height: '72px',
  },

  '@media (max-width: 1440px)': {
    width: '64px',
    height: '64px',
  },

  '@media (max-width: 1200px)': {
    width: '60px',
    height: '60px',
  },

  '@media (max-width: 768px)': {
    width: '48px',
    height: '48px',
    top: 'calc(50% + 30px)',
  },
});

const LeftNavIcon = styled(NavIcon)({
  left: '0',

  '@media (max-width: 768px)': {
    left: '0',
  },
});

const RightNavIcon = styled(NavIcon)({
  right: '0',

  '@media (max-width: 768px)': {
    right: '0',
  },
});

const ProductsSlider = styled(Box)({
  position: 'relative',
  width: '50%',
  margin: '0 auto',
  overflow: 'visible',

  '@media (max-width: 1600px)': {
    width: '55%',
  },

  '@media (max-width: 1440px)': {
    width: '60%',
  },

  '@media (max-width: 1200px)': {
    width: '65%',
  },

  '@media (max-width: 968px)': {
    width: '75%',
  },

  '@media (max-width: 768px)': {
    width: '85%',
  },
});

const ProductCard = styled(motion.div)({
  position: 'relative',
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '0.85fr 1.15fr',
  gap: '32px',
  alignItems: 'start',

  '@media (max-width: 1600px)': {
    gap: '32px',
  },

  '@media (max-width: 1440px)': {
    gap: '32px',
  },

  '@media (max-width: 1200px)': {
    gap: '32px',
  },

  '@media (max-width: 968px)': {
    gridTemplateColumns: '1fr',
    gap: '32px',
  },
});

const ProductImageContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '60vh',
  borderRadius: '2px',
  overflow: 'hidden',

  '@media (max-width: 968px)': {
    height: 'auto',
    paddingTop: '75%',
  },
});

const ProductImage = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const ProductInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  height: '60vh',

  '@media (max-width: 1440px)': {
    gap: '18px',
  },

  '@media (max-width: 968px)': {
    height: 'auto',
    gap: '20px',
  },
});

const ProductHeader = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const ProductTitle = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '60px',
  fontWeight: 300,
  color: designSystem.colors.grayText,
  lineHeight: 1.3,
  letterSpacing: '0.6px',

  '@media (max-width: 1600px)': {
    fontSize: '54px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '48px',
  },

  '@media (max-width: 1200px)': {
    fontSize: '42px',
  },

  '@media (max-width: 968px)': {
    fontSize: '38px',
  },

  '@media (max-width: 768px)': {
    fontSize: '32px',
  },
});

const ProductSubtitle = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.ja,
  fontSize: '24px',
  fontWeight: 300,
  color: designSystem.colors.grayText,
  lineHeight: 1.4,
  letterSpacing: '0.24px',

  '@media (max-width: 1600px)': {
    fontSize: '22px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '20px',
  },

  '@media (max-width: 1200px)': {
    fontSize: '18px',
  },

  '@media (max-width: 768px)': {
    fontSize: '16px',
  },
});

const Divider = styled(Box)({
  width: '100%',
  height: '1px',
  backgroundColor: designSystem.colors.grayText,
  opacity: 0.3,
  margin: '0',
});

const OverviewSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '0 8px',
  flex: 1,
  overflow: 'hidden',

  '@media (max-width: 1440px)': {
    gap: '10px',
  },

  '@media (max-width: 968px)': {
    gap: '10px',
    overflow: 'visible',
  },
});

const OverviewTitle = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '16px',
  fontWeight: 300,
  color: designSystem.colors.grayText,
  lineHeight: 1.4,
  letterSpacing: '0.16px',

  '@media (max-width: 1440px)': {
    fontSize: '14px',
  },
});

const OverviewText = styled(Typography)({
  fontFamily: designSystem.typography.fontFamily.ja,
  fontSize: '18px',
  fontWeight: 400,
  color: designSystem.colors.grayText,
  lineHeight: 1.8,
  letterSpacing: '0.18px',

  '@media (max-width: 1600px)': {
    fontSize: '17px',
    lineHeight: 1.75,
  },

  '@media (max-width: 1440px)': {
    fontSize: '16px',
    lineHeight: 1.7,
  },

  '@media (max-width: 1200px)': {
    fontSize: '15px',
    lineHeight: 1.65,
  },

  '@media (max-width: 768px)': {
    fontSize: '14px',
    lineHeight: 1.6,
  },
});

const products = [
  {
    id: 1,
    title: 'Ucart',
    subtitle: '韓国サイトの商品を簡単に買えるECアプリ',
    overview: '「誰もが等しく買い物をできる社会の実現」という理念のもと生まれた、韓国ECサイトの商品を簡単に購入できるアプリ。\n現地住所がなければ公式サイトやECサービスから購入できないという課題に対し、裏側では自動で代理購入処理を行う仕組みを構築。\nユーザーは、国やサイトを跨いでもひとつのカートで商品をまとめ、通常のオンラインショッピングと変わらない体験を得られます。\n代理購入による不透明なマージン構造をなくし、より公平で開かれた購買体験を実現しました。',
    image: ucartImage,
  },
  {
    id: 2,
    title: 'Fairs',
    subtitle: '中国激安雑貨マーケットプレイス',
    overview: '多様な商品を取り扱うグローバルなECプラットフォーム。中国の商品を手軽に購入できる仕組みを提供し、国境を越えたショッピング体験を実現しました。',
    image: fairsImage,
  },
  {
    id: 3,
    title: 'Wuu',
    subtitle: 'ゲーム通販アプリケーション',
    overview: 'ゲーマーのための直感的で使いやすいショッピング体験を提供するアプリケーション。最新のゲームタイトルから周辺機器まで、幅広い商品を取り扱っています。',
    image: wuuImage,
  },
  {
    id: 4,
    title: 'Synchro',
    subtitle: 'YouTube 同時視聴 SNS',
    overview: '友達と一緒に動画を楽しむ新しいソーシャル体験。リアルタイムで同期した視聴を可能にし、チャット機能を通じて感想を共有できます。',
    image: synchroImage,
  },
  {
    id: 5,
    title: 'Fairs Admin',
    subtitle: 'Fairs 管理画面',
    overview: '効率的な運営を実現する包括的な管理システム。商品管理、注文処理、ユーザー管理など、ECサイト運営に必要な機能を一元管理できます。',
    image: fairsAdminImage,
  },
];

const ProjectsSection = () => {
  const [ref] = useScrollAnimation({ threshold: 0.2 });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const handlePrev = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < products.length - 1) {
      setDirection(1);
      setCurrentIndex(currentIndex + 1);
    }
  };

  const currentProduct = products[currentIndex];

  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.01, 0.05, 0.95],
      },
    },
    exit: (dir) => ({
      x: dir > 0 ? -1000 : 1000,
      opacity: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.01, 0.05, 0.95],
      },
    }),
  };

  return (
    <ProductsContainer id="projects" ref={ref}>
      <ProductsContent>
        <SectionHeader>
          <SectionTitle>EX-PRODUCTS</SectionTitle>
          <ProductCounter>
            <CounterText active>{String(currentIndex + 1).padStart(2, '0')}</CounterText>
            <CounterDivider />
            <CounterText>{String(products.length).padStart(2, '0')}</CounterText>
          </ProductCounter>
        </SectionHeader>

        {/* Left Navigation Icon */}
        <LeftNavIcon
          src="/icon_left.png"
          alt="Previous"
          onClick={currentIndex === 0 ? undefined : handlePrev}
          style={{ 
            opacity: currentIndex === 0 ? 0.3 : 1,
            cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
            pointerEvents: currentIndex === 0 ? 'none' : 'auto'
          }}
        />

        {/* Right Navigation Icon */}
        <RightNavIcon
          src="/icon_right.png"
          alt="Next"
          onClick={currentIndex === products.length - 1 ? undefined : handleNext}
          style={{ 
            opacity: currentIndex === products.length - 1 ? 0.3 : 1,
            cursor: currentIndex === products.length - 1 ? 'not-allowed' : 'pointer',
            pointerEvents: currentIndex === products.length - 1 ? 'none' : 'auto'
          }}
        />

        <ProductsSlider>
          <AnimatePresence mode="wait" custom={direction}>
            <ProductCard
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <ProductImageContainer>
                <ProductImage src={currentProduct.image} alt={currentProduct.title} />
              </ProductImageContainer>

              <ProductInfo>
                <ProductHeader>
                  <ProductTitle>{currentProduct.title}</ProductTitle>
                  <ProductSubtitle>{currentProduct.subtitle}</ProductSubtitle>
                </ProductHeader>
                <Divider />
                <OverviewSection>
                  <OverviewTitle>OVERVIEW</OverviewTitle>
                  <OverviewText>
                    {currentProduct.overview.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < currentProduct.overview.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </OverviewText>
                </OverviewSection>
              </ProductInfo>
            </ProductCard>
          </AnimatePresence>
        </ProductsSlider>
      </ProductsContent>
    </ProductsContainer>
  );
};

export default ProjectsSection;
